// Recidiviz - a data platform for criminal justice reform
// Copyright (C) 2025 Recidiviz, Inc.
//
// This program is free software: you can redistribute it and/or modify
// it under the terms of the GNU General Public License as published by
// the Free Software Foundation, either version 3 of the License, or
// (at your option) any later version.
//
// This program is distributed in the hope that it will be useful,
// but WITHOUT ANY WARRANTY; without even the implied warranty of
// MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
// GNU General Public License for more details.
//
// You should have received a copy of the GNU General Public License
// along with this program.  If not, see <https://www.gnu.org/licenses/>.
// =============================================================================

import { matchPath } from "react-router-dom";

import { EdovoLandingPage } from "~@jii/paths";

import { windowIsIframe } from "./iframe";

// we run under a custom edovo subdomain to pass through network filters
export function isEdovoSubdomain() {
  return window.location.hostname.endsWith(".edovo.com");
}

export function isEdovoEnv(): boolean {
  if (isEdovoSubdomain()) return true;

  // wrapping in try/catch to avoid CORS errors in case we encounter a parent
  // that doesn't allow us to access location.hostname (expected to be someone
  // other than Edovo)
  try {
    // testing environments may not iframe the custom domain URL
    if (
      windowIsIframe() &&
      // could be various domains under edovo.com or tedovo.com
      window.parent.location.hostname.endsWith("edovo.com")
    )
      return true;
  } catch {
    // fall through
  }
  // force the value if we are currently on the edovo landing page
  if (matchPath(EdovoLandingPage.path, window.location.pathname)) return true;

  return false;
}

// the Edovo entries from frame-ancestors in firebase.jii.json
const EDOVO_PARENT_DOMAINS = [".edovo.com", ".tedovo.com", ".learnedovo.com"];

/** The origin of the Edovo page framing us, or undefined if it isn't one */
function parentEdovoOrigin() {
  if (!document.referrer) return undefined;
  const { origin, hostname } = new URL(document.referrer);
  if (!EDOVO_PARENT_DOMAINS.some((domain) => hostname.endsWith(domain)))
    return undefined;
  return origin;
}

/**
 * Asks Edovo's page to take the resident to its courses. A random id is sent.
 * The page to return to stays in localStorage until Edovo echoes the id back.
 * Returns true only if the message was actually sent.
 */
export function sendEdovoReferral(): boolean {
  const edovoOrigin = parentEdovoOrigin();
  if (!edovoOrigin || !isEdovoEnv() || !windowIsIframe()) {
    console.warn(
      "sendEdovoReferral: skipped because the app is not running inside Edovo's iframe",
    );
    return false;
  }
  const sessionResumeId = crypto.randomUUID();
  const returnState = {
    sessionResumeId,
    returnPath: window.location.pathname,
  };
  localStorage.setItem("edovoReturnState", JSON.stringify(returnState));
  window.parent.postMessage(
    { type: "program-navigation", sessionResumeId },
    edovoOrigin,
  );
  return true;
}

/** Compares the sessionResumeId Edovo echoed back to what we saved, then clears it.*/
export function consumeEdovoReferralReturnPath(sessionResumeId?: string) {
  try {
    const saved = localStorage.getItem("edovoReturnState");
    localStorage.removeItem("edovoReturnState");
    if (!sessionResumeId || !saved) return undefined;
    const returnState = JSON.parse(saved);
    if (returnState.sessionResumeId !== sessionResumeId) return undefined;
    return returnState.returnPath;
  } catch {
    return undefined;
  }
}
